import { useState } from "react";
import { useTenantTerms } from "../../../lib/TenantConfigContext";
import {
  peopleAtWorkshop,
  personRemovalNote,
  snapshotRoleLabel,
  unfilledRoles,
  workshopRow,
} from "../../../lib/organization";
import { usePersonRemoval } from "../../SuperAdmin/useOrgEditors";
import { Icon } from "../../../components/msd/Icon";
import { Avatar, Btn, LinkBtn, Sheet, cx } from "../../../components/msd/ui";
import PersonSheet from "./PersonSheet";
import {
  ConfirmDelete,
  Picker,
  RemoveBtn,
  SETTINGS_GROUP,
  SETTINGS_ROW,
  plural,
} from "./kit";

const STATUS = [
  ["active", "Active"],
  ["paused", "Paused"],
];

/**
 * One workshop: who works there, whether it is live, and deleting it — what
 * Super Admin's WorkshopDetail and the Workshops row offer, in a sheet.
 *
 * @param editor  useWorkshopEditor(), shared with the page
 */
export default function WorkshopSheet({
  ix,
  workshopId,
  onClose,
  editor,
  canEdit,
  canEditPeople,
  tenantId = null,
}) {
  const { roleLabel: contextRoleLabel } = useTenantTerms();
  const roleLabel = (code) => snapshotRoleLabel(ix, code, contextRoleLabel);
  const removal = usePersonRemoval(tenantId, ix);
  // The person in the person sheet: false when closed, null when adding.
  const [person, setPerson] = useState(false);

  const workshop = workshopId ? ix.workshopById.get(workshopId) : null;
  if (!workshop) return null;

  const location = workshop.workshop_location;
  const row = workshopRow(workshop, ix);
  // Service advisors are listed with everyone else: same row, same edit and
  // remove, so there is one way to look after anyone at the workshop.
  const staff = peopleAtWorkshop(location, ix);
  const unfilled = unfilledRoles(location, ix);
  const dealer = workshop.dealer_id
    ? ix.dealerById.get(workshop.dealer_id)
    : null;
  const paused = row.status === "paused";

  const deleteWorkshop = async () => {
    if (await editor.confirmDelete()) onClose();
  };
  const removePerson = async () => {
    if (await removal.confirmRemove()) setPerson(false);
  };

  // One thing on screen at a time, so Escape closes only the top one.
  const covered = person !== false || !!removal.deleting || !!editor.deleting;

  return (
    <>
      <Sheet
        open={!covered}
        title={location}
        onClose={onClose}
        testid="workshop-sheet"
        footer={
          <>
            {canEdit && (
              <RemoveBtn onClick={() => editor.askDelete(workshop.id)}>
                Delete workshop
              </RemoveBtn>
            )}
            <Btn variant="pri" onClick={onClose}>
              Done
            </Btn>
          </>
        }
      >
        <div className="text-[13.5px] text-msd-muted">
          {dealer ? dealer.name : "No dealer"}
        </div>

        <div className={SETTINGS_GROUP}>
          <div className={SETTINGS_ROW}>
            <span className="grow text-[14.5px] text-msd-ink">Status</span>
            {/* Takes effect at once: pausing stops new bookings at this workshop. */}
            <Picker
              label="Workshop status"
              options={STATUS}
              value={paused ? "paused" : "active"}
              disabled={!canEdit}
              onChange={() => editor.toggleStatus(row)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between px-1">
            <span className="text-[14px] font-semibold text-msd-ink">Team</span>
            <span className="text-[12.5px] text-msd-muted">
              {plural(staff.length, "person", "people")}
            </span>
          </div>
          {/* The roles nobody fills, as one compact block above the team: a tap adds someone. */}
          {unfilled.length > 0 && (
            <div className="flex flex-col gap-2 rounded-[12px] bg-[#FFFAEB] px-4 py-3">
              <span className="flex items-center gap-[6px] text-[13px] font-medium text-[#B54708]">
                <Icon name="warn" size={13} stroke={2.4} />
                Not filled
              </span>
              <div className="flex flex-wrap gap-[6px]">
                {unfilled.map((k) =>
                  canEditPeople ? (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setPerson(null)}
                      aria-label={`Add ${roleLabel(k)}`}
                      className="flex h-7 cursor-pointer items-center gap-1 rounded-full border border-[#FEDF89] bg-white px-[10px] text-[12.5px] text-[#93370D] hover:border-[#F79009]"
                    >
                      <Icon name="plus2" size={11} stroke={2.6} />
                      {roleLabel(k)}
                    </button>
                  ) : (
                    <span
                      key={k}
                      className="flex h-7 items-center rounded-full border border-[#FEDF89] bg-white px-[10px] text-[12.5px] text-[#93370D]"
                    >
                      {roleLabel(k)}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
          <div className={SETTINGS_GROUP}>
            {staff.map((p) => {
              const name = p.name || (p.email || "").split("@")[0];
              const inner = (
                <>
                  <Avatar name={name} size={30} className="bg-msd-surface" />
                  <span className="min-w-0 grow">
                    <span className="block truncate text-[14.5px] text-msd-ink">
                      {name}
                    </span>
                    {/* A login named after its role would say the same thing twice. */}
                    {roleLabel(p.role) !== name && (
                      <span className="block truncate text-[12.5px] text-msd-muted">
                        {roleLabel(p.role)}
                      </span>
                    )}
                  </span>
                </>
              );
              return canEditPeople ? (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPerson(p)}
                  aria-label={`Edit ${p.name || p.email}`}
                  data-testid="workshop-person"
                  className={cx(
                    SETTINGS_ROW,
                    "w-full cursor-pointer border-x-0 border-b-0 bg-transparent text-left text-inherit hover:bg-msd-soft",
                  )}
                >
                  {inner}
                  <span className="flex text-msd-faint">
                    <Icon name="right" size={14} stroke={2} />
                  </span>
                </button>
              ) : (
                <div
                  key={p.id}
                  className={SETTINGS_ROW}
                  data-testid="workshop-person"
                >
                  {inner}
                </div>
              );
            })}
            {canEditPeople && (
              <button
                type="button"
                onClick={() => setPerson(null)}
                className={cx(
                  SETTINGS_ROW,
                  "w-full cursor-pointer border-x-0 border-b-0 bg-transparent text-left text-[14.5px] font-medium text-[#3A5F94] hover:bg-msd-soft",
                )}
              >
                <span className="flex h-[30px] w-[30px] items-center justify-center">
                  <Icon name="plus2" size={16} stroke={2.4} />
                </span>
                Add person
              </button>
            )}
            {!staff.length && !unfilled.length && !canEditPeople && (
              <div className={cx(SETTINGS_ROW, "text-[14px] text-msd-muted")}>
                No one yet
              </div>
            )}
          </div>
        </div>
      </Sheet>

      <PersonSheet
        tenantId={tenantId}
        open={person !== false && !removal.deleting}
        onOpenChange={(v) => !v && setPerson(false)}
        ix={ix}
        person={person || null}
        atWorkshop={person ? null : location}
        onRemove={(p) => removal.askRemove(p.id, p.has_login)}
      />

      <ConfirmDelete
        open={!!removal.deleting}
        onClose={() => removal.setDeleting(null)}
        name={removal.deleting?.name || ""}
        body={personRemovalNote(removal.deleting?.hasLogin)}
        onConfirm={removePerson}
        pending={removal.pending}
      />

      <ConfirmDelete
        open={!!editor.deleting}
        onClose={() => editor.setDeleting(null)}
        name={editor.deleting?.name || ""}
        body={editor.deleting?.note || "This cannot be undone."}
        blocked={editor.deleting?.blocked}
        onConfirm={deleteWorkshop}
        pending={editor.pending}
      />
    </>
  );
}
